import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable } from 'rxjs';
import { IEnforcementListApi } from '../models/enforcement-api.model';
import { HttpParams } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { AdvancedSearchCheckboxes, AdvancedSearchConfigResponse, AdvancedSearchOption } from '@carm/shared/components/advanced-search/models/advanced-search.model';
import { map } from 'rxjs/operators';
import { LoaderSpinnerTypes } from '@carm/_core/models/loader-types.model';

@Injectable()
export class EnforcementService extends BaseApiService {
  constructor(protected injector: Injector, private datePipe: DatePipe) {
    super(injector);
  }
  private get serviceBaseUrlBP(): string {
    return `${this.endPointBP}/enforcement`;
  }

  public getEnforcementList(
    tabSelected: string,
    minDate: Date,
    maxDate: Date,
    sort: string,
    pageIndex: number,
    pageSize: number,
    searchString: string
  ): Observable<IEnforcementListApi> {
    const params = new HttpParams()
      .set('currentPage', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('sort', sort)
      .set('searchString', searchString || null)
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'));
    const endPoint = `${this.serviceBaseUrlBP}/${tabSelected}/list`;
    return this.httpAuthGet<IEnforcementListApi>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  public getPenaltiesList(
    minDate: Date,
    maxDate: Date,
    sort: string,
    pageIndex: number,
    pageSize: number,
    searchString: string
  ): Observable<IEnforcementListApi> {

    const params = new HttpParams()
      .set('currentPage', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('sort', sort)
      .set('searchString', searchString || null)
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'));

    const endPoint = `${this.serviceBaseUrlBP}/penalties/list`;
    return this.httpAuthGet<IEnforcementListApi>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  getPenaltiesSearchOptions(): Observable<AdvancedSearchConfigResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/penalties/search-options`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint).pipe(
      map((advanceSearch: AdvancedSearchConfigResponse) => {
        return {
          searchOptions: advanceSearch.searchOptions?.map(
            (advanceSearchOption: AdvancedSearchOption) => {
              advanceSearchOption.key = advanceSearchOption.value; // api return number for key. use identifier
              advanceSearchOption.value = this.translateService.instant(
                `concepts.rulings.advanceSearchOptions.${advanceSearchOption.value}`
              );
              return advanceSearchOption;
            }
          ),
          checkboxes: advanceSearch.checkboxes?.map(
            (checkboxes: AdvancedSearchCheckboxes) => {
              checkboxes.value = this.translateService.instant(
                `concepts.rulings.advanceSearchOptions.${checkboxes.value}`
              );
              return checkboxes;
            }
          ),
        } as AdvancedSearchConfigResponse;
      })
    );
  }

  getSeizuresSearchOptions(): Observable<AdvancedSearchConfigResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/seizures-forfeitures/search-options`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint).pipe(
      map((advanceSearch: AdvancedSearchConfigResponse) => {
        return {
          searchOptions: advanceSearch.searchOptions?.map(
            (advanceSearchOption: AdvancedSearchOption) => {
              advanceSearchOption.key = advanceSearchOption.value; // api return number for key. use identifier
              advanceSearchOption.value = this.translateService.instant(
                `concepts.rulings.advanceSearchOptions.${advanceSearchOption.value}`
              );
              return advanceSearchOption;
            }
          ),
          checkboxes: advanceSearch.checkboxes?.map(
            (checkboxes: AdvancedSearchCheckboxes) => {
              checkboxes.value = this.translateService.instant(
                `concepts.rulings.advanceSearchOptions.${checkboxes.value}`
              );
              return checkboxes;
            }
          ),
        } as AdvancedSearchConfigResponse;
      })
    );
  }

  getCorrectionsSearchOptions(): Observable<AdvancedSearchConfigResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/corrections/search-options`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint).pipe(
      map((advanceSearch: AdvancedSearchConfigResponse) => {
        return {
          searchOptions: advanceSearch.searchOptions?.map(
            (advanceSearchOption: AdvancedSearchOption) => {
              advanceSearchOption.key = advanceSearchOption.value; // api return number for key. use identifier
              advanceSearchOption.value = this.translateService.instant(
                `concepts.rulings.advanceSearchOptions.${advanceSearchOption.value}`
              );
              return advanceSearchOption;
            }
          ),
          checkboxes: advanceSearch.checkboxes?.map(
            (checkboxes: AdvancedSearchCheckboxes) => {
              checkboxes.value = this.translateService.instant(
                `concepts.rulings.advanceSearchOptions.${checkboxes.value}`
              );
              return checkboxes;
            }
          ),
        } as AdvancedSearchConfigResponse;
      })
    );
  }
}
